/**
 * ModuleArrayPage.tsx — Multi-Module Array Plan-View Visualization
 *
 * Shows 2–6 diffuser modules in a configurable grid arrangement.
 * All module design parameters are inherited from DiffuserModulePage via
 * sessionStorage (rb_diffuser_module_v1) — no data re-entry required.
 *
 * Visualises (plan view — top-down):
 *   · Each 2×2 m frame with exact diffuser positions and count from design page
 *   · Surface plume footprint per diffuser (Seol cone at water surface)
 *   · Influence radius circles (coverage zone at bed level)
 *   · Inter-module gap dimension annotations
 *   · Overlap zones where adjacent frames' coverages interact
 *   · Combined bed velocity (Shields) in gap zones — disturbance risk
 *   · Total O₂ balance and coverage over array footprint
 *   · Design recommendations for gap distance
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Layers, ArrowLeft, Wind, RotateCcw, AlertTriangle,
  CheckCircle2, Info, Grid3x3, Activity, Database,
  ChevronDown, ChevronUp, Ruler, Gauge, Circle, FileText,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { generateModuleArrayPDF } from '@/lib/reservoirPdf';
import { loadPersisted, KEYS, savePersisted } from '@/lib/sessionPersistence';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';

// ─── Session storage keys ─────────────────────────────────────────────────────
const MODULE_KEY = 'rb_diffuser_module_v1';
const ARRAY_KEY  = 'rb_module_array_v1';

// ─── Physics constants ────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const FRAME_SIZE   = 2.0;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const GAMMA_AIR    = 1.4;

// ─── Physics functions (identical to DiffuserModulePage) ──────────────────────

function waterDensity(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T*T
       + 1.001685e-4*T*T*T - 1.120083e-6*T*T*T*T;
}
function waterViscosity(T: number) {
  return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15));
}
function waterSurfaceTension(T: number) {
  return Math.max(0.04, 0.07564 - 1.376e-4 * T);
}
function waterKinVisc(T: number) { return waterViscosity(T) / waterDensity(T); }
function doSat(T: number)        { return 468 / (31.6 + T); }

function bubbleDiamMm(poreSizeUm: number, airflowNm3h: number, pores: number, T: number) {
  const d_pore = poreSizeUm / 1e6;
  const sigma  = waterSurfaceTension(T);
  const rho_w  = waterDensity(T);
  const d_stat = Math.pow((6 * sigma * d_pore) / (rho_w * G), 1/3);
  const q_pore = (airflowNm3h / 3600) / Math.max(1, pores);
  return d_stat * (1 + 0.9 * Math.pow(Math.max(q_pore * 1e9, 0.001), 0.25)) * 1000;
}
function riseVelocityMs(d_mm: number, T: number) {
  let v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm : d_mm <= 3.0 ? 0.14 + 0.065*d_mm : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm-3) : 0.395;
  return v0 * Math.pow(waterKinVisc(20) / waterKinVisc(T), 0.7);
}
function bubbleSurface(d_mm: number, depthM: number, T: number) {
  return d_mm * Math.pow((P_ATM + waterDensity(T) * G * depthM) / P_ATM, 1/3);
}
function calcSOTR(nm3h: number, sote: number) { return nm3h * RHO_AIR_STD * O2_MASS_FRAC * sote; }
function calcAOTR(sotr: number, alpha: number, beta: number, T: number, doMgL: number) {
  return sotr * alpha * beta * Math.pow(1.024, T-20) * Math.max(0, doSat(T) - doMgL) / DO_SAT_20;
}
function blowerKw(nm3h: number, submM: number, dropKpa: number, safetyPct: number, eff: number) {
  const Q    = nm3h / 3600;
  const P_in = P_ATM;
  const P_out= P_in + (waterDensity(20)*G*submM + dropKpa*1000) * (1 + safetyPct/100);
  const exp  = (GAMMA_AIR-1)/GAMMA_AIR;
  return (GAMMA_AIR/(GAMMA_AIR-1)) * P_in * Q * (Math.pow(P_out/P_in, exp)-1) / eff / 1000;
}

// ─── Layout functions (identical to DiffuserModulePage) ───────────────────────

type DiffQty = 4 | 6 | 8 | 10 | 12;
interface DiffPos { x: number; y: number }

function clampCornerOffset(c: number) { return Math.min(Math.max(0.10, c), FRAME_SIZE * 0.40); }

function diffuserLayout(count: DiffQty, cornerOffset: number): DiffPos[] {
  const L = FRAME_SIZE, c = clampCornerOffset(cornerOffset), mid = L/2;
  switch (count) {
    case 4:  return [{x:c,y:c},{x:L-c,y:c},{x:c,y:L-c},{x:L-c,y:L-c}];
    case 6:  return [{x:c,y:c},{x:L-c,y:c},{x:c,y:mid},{x:L-c,y:mid},{x:c,y:L-c},{x:L-c,y:L-c}];
    case 8:  return [{x:c,y:c},{x:L-c,y:c},{x:c,y:L-c},{x:L-c,y:L-c},{x:mid,y:c},{x:mid,y:L-c},{x:c,y:mid},{x:L-c,y:mid}];
    case 10: { const t=c+(L-2*c)/3; return [{x:c,y:c},{x:L-c,y:c},{x:c,y:L-c},{x:L-c,y:L-c},{x:mid,y:c},{x:mid,y:L-c},{x:c,y:mid},{x:L-c,y:mid},{x:t,y:mid},{x:L-t,y:mid}]; }
    case 12: { const s=(L-2*c)/3,rows=[c,c+s,L-c-s,L-c]; return rows.flatMap(ry=>[{x:c,y:ry},{x:mid,y:ry},{x:L-c,y:ry}]); }
  }
}

function minDiffSpacingM(count: DiffQty, cornerOffset: number) {
  const L=FRAME_SIZE, c=clampCornerOffset(cornerOffset), inn=L-2*c;
  return count===4 ? inn : (count===6||count===8) ? L/2-c : inn/3;
}

// ─── Diffuser type data ───────────────────────────────────────────────────────

const DIFF_TYPES = [
  { value: 'membrane_disc_epdm', label: 'EPDM Membrane Disc', sote: 6.5, pores: (d:number)=>Math.round(0.35*Math.PI*(d/2)**2) },
  { value: 'membrane_disc_ptfe', label: 'PTFE Membrane Disc', sote: 7.0, pores: (d:number)=>Math.round(0.40*Math.PI*(d/2)**2) },
  { value: 'ceramic_dome',       label: 'Ceramic Dome',        sote: 5.5, pores: (d:number)=>Math.round(0.10*Math.PI*(d/2)**2) },
  { value: 'hdpe_porous_tube',   label: 'HDPE Porous Tube',   sote: 5.0, pores: (d:number)=>Math.round(0.08*d*500) },
];

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface ModuleSpec {
  diffuserCount:      DiffQty;
  cornerOffsetM:      number;
  mountingHeightM:    number;
  diffuserType:       string;
  diffuserDiamMm:     number;
  poreSizeUm:         number;
  airflowPerDiffNm3h: number;
  diffuserDropKpa:    number;
  waterDepthM:        number;
  waterTempC:         number;
  currentDoMgL:       number;
  alphaFactor:        number;
  betaFactor:         number;
  sedimentD50Um:      number;
  sedimentRho:        number;
  sodGO2m2d:          number;
  blowerEff:          number;
  safetyPct:          number;
  // Tube-specific (optional)
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
}

const MODULE_DEFAULTS: ModuleSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 5.0, waterTempC: 15, currentDoMgL: 2.0,
  alphaFactor: 0.80, betaFactor: 0.98,
  sedimentD50Um: 100, sedimentRho: 2650, sodGO2m2d: 1.0,
  blowerEff: 0.70, safetyPct: 10,
};

interface ArrayConfig {
  moduleCount:   number;   // 2–6
  cols:          number;   // 1, 2, or 3
  gapXM:         number;   // horizontal gap (m)
  gapYM:         number;   // vertical gap (m)
  showInfluence: boolean;
  showSurface:   boolean;
  showLabels:    boolean;
  reservoirAreaHa: number;
}

const ARRAY_DEFAULTS: ArrayConfig = {
  moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0,
  showInfluence: true, showSurface: true, showLabels: true,
  reservoirAreaHa: 5.0,
};

// ─── Array SVG component ──────────────────────────────────────────────────────

interface WorldDiff { wx: number; wy: number; mi: number; di: number }
interface FramePos  { fx: number; fy: number; mi: number }

function ArraySVG({
  spec, cfg, framePositions, localPositions, allDiffusers,
  r_inf, r_plume_surface, effSubm,
}: {
  spec: ModuleSpec; cfg: ArrayConfig;
  framePositions: FramePos[]; localPositions: DiffPos[];
  allDiffusers: WorldDiff[];
  r_inf: number; r_plume_surface: number; effSubm: number;
}) {
  const SVG_W = 700, SVG_H = 460, PAD = 44;
  const DRAW_W = SVG_W - PAD * 2, DRAW_H = SVG_H - PAD * 2;

  const cols = cfg.cols;
  const rows = Math.ceil(cfg.moduleCount / cols);

  const arrayW = cols * FRAME_SIZE + (cols - 1) * cfg.gapXM;
  const arrayH = rows * FRAME_SIZE + (rows - 1) * cfg.gapYM;

  const scaleX = DRAW_W / arrayW;
  const scaleY = DRAW_H / arrayH;
  const scale  = Math.min(scaleX, scaleY);

  // Centre the drawing
  const offX = PAD + (DRAW_W - arrayW * scale) / 2;
  const offY = PAD + (DRAW_H - arrayH * scale) / 2;

  const sx = (m: number) => offX + m * scale;
  const sy = (m: number) => offY + m * scale;

  const diffR   = Math.max(3, (spec.diffuserDiamMm / 1000) * scale * 0.75);
  const r_inf_px = r_inf * scale;
  const r_sp_px  = r_plume_surface * scale;
  const cPx      = clampCornerOffset(spec.cornerOffsetM) * scale;

  // Colors
  const CYAN    = '#06b6d4';
  const VIOLET  = '#8b5cf6';
  const ORANGE  = '#f97316';
  const FRAME_C = '#0ea5e9';

  return (
    <svg width={SVG_W} height={SVG_H}
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      className="w-full rounded-xl border border-slate-800/60">
      {/* Background */}
      <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="#0a0f1e" rx={12} />

      {/* Water body backdrop */}
      <rect x={offX - 8} y={offY - 8}
        width={arrayW * scale + 16} height={arrayH * scale + 16}
        fill="#0c1a2e" rx={4} />

      {/* ── Surface plume footprints (rendered under frames) ─────────────── */}
      {cfg.showSurface && allDiffusers.map((d, i) => (
        <circle key={`sp-${i}`}
          cx={sx(d.wx)} cy={sy(d.wy)} r={r_sp_px}
          fill={CYAN} fillOpacity={0.10}
          stroke={CYAN} strokeOpacity={0.25} strokeWidth={0.6} />
      ))}

      {/* ── Influence radius circles ──────────────────────────────────────── */}
      {cfg.showInfluence && allDiffusers.map((d, i) => (
        <circle key={`inf-${i}`}
          cx={sx(d.wx)} cy={sy(d.wy)} r={r_inf_px}
          fill="none"
          stroke={FRAME_C} strokeOpacity={0.18} strokeWidth={0.8}
          strokeDasharray="4,4" />
      ))}

      {/* ── Frame rectangles ──────────────────────────────────────────────── */}
      {framePositions.map((fp) => {
        const x = sx(fp.fx), y = sy(fp.fy);
        const w = FRAME_SIZE * scale, h = FRAME_SIZE * scale;
        return (
          <g key={`fr-${fp.mi}`}>
            {/* Frame border */}
            <rect x={x} y={y} width={w} height={h}
              fill="#061220" fillOpacity={0.8}
              stroke={FRAME_C} strokeWidth={1.8} rx={2} />
            {/* Corner offset annotation lines (TL corner) */}
            <line x1={x} y1={y} x2={x + cPx} y2={y}
              stroke={ORANGE} strokeWidth={0.8} strokeDasharray="2,2" opacity={0.7} />
            <line x1={x} y1={y} x2={x} y2={y + cPx}
              stroke={ORANGE} strokeWidth={0.8} strokeDasharray="2,2" opacity={0.7} />
            {/* Air header ring */}
            <rect x={x + 3} y={y + 3} width={w - 6} height={h - 6}
              fill="none" stroke="#78350f" strokeWidth={0.8} strokeDasharray="3,3" rx={2} opacity={0.5} />
            {/* Module label */}
            {cfg.showLabels && (
              <text x={x + w / 2} y={y + h - 5} textAnchor="middle"
                fontSize={Math.max(8, scale * 0.12)} fill="#1e3a5f" fontWeight="bold">
                M{fp.mi + 1}
              </text>
            )}
          </g>
        );
      })}

      {/* ── Diffuser rendering — TYPE-AWARE (tubes or discs) ───────────────── */}
      {(() => {
        const isTubeType = spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube';
        if (isTubeType) {
          // Render TUBES for each module: parallel tubes along the frame
          const tubeCount = Math.min(4, Math.max(1, spec.tubeCount ?? 2));
          const tubeLen   = Math.min(2.0, spec.tubeLengthM ?? 1.8);
          const pat       = spec.tubePattern ?? 'parallel';
          const off       = Math.max(0.05, Math.min(0.95, spec.tubeCentreOffsetM ?? 0.5));
          return framePositions.map(fp => {
            const fpX0 = fp.fx, fpY0 = fp.fy;  // top-left of frame in module-coords
            const c = (FRAME_SIZE - tubeLen) / 2;
            const segs: { x1: number; y1: number; x2: number; y2: number }[] = [];
            if (pat === 'perimeter') {
              const inset = 0.1;
              const list = [
                { x1: c, y1: inset, x2: c + tubeLen, y2: inset },
                { x1: c, y1: FRAME_SIZE - inset, x2: c + tubeLen, y2: FRAME_SIZE - inset },
                { x1: inset, y1: c, x2: inset, y2: c + tubeLen },
                { x1: FRAME_SIZE - inset, y1: c, x2: FRAME_SIZE - inset, y2: c + tubeLen },
              ];
              for (let i = 0; i < tubeCount; i++) segs.push(list[i]);
            } else if (pat === 'cross') {
              const mid = FRAME_SIZE / 2;
              const list = [
                { x1: c, y1: mid, x2: c + tubeLen, y2: mid },
                { x1: mid, y1: c, x2: mid, y2: c + tubeLen },
              ];
              for (let i = 0; i < tubeCount; i++) segs.push(list[i]);
            } else if (pat === 'centre_radial') {
              const mid = FRAME_SIZE / 2;
              for (let i = 0; i < tubeCount; i++) {
                const ang = (i / tubeCount) * 2 * Math.PI;
                segs.push({
                  x1: mid, y1: mid,
                  x2: mid + Math.cos(ang) * tubeLen / 2, y2: mid + Math.sin(ang) * tubeLen / 2,
                });
              }
            } else {
              // parallel
              if (tubeCount === 1) {
                segs.push({ x1: c, y1: FRAME_SIZE / 2, x2: c + tubeLen, y2: FRAME_SIZE / 2 });
              } else {
                for (let i = 0; i < tubeCount; i++) {
                  const sign = (i % 2 === 0) ? -1 : 1;
                  const idx = Math.floor(i / 2) + 1;
                  segs.push({
                    x1: c, y1: FRAME_SIZE / 2 + sign * off * idx,
                    x2: c + tubeLen, y2: FRAME_SIZE / 2 + sign * off * idx,
                  });
                }
              }
            }
            return segs.map((seg, i) => {
              const x1s = sx(fpX0 + seg.x1), y1s = sy(fpY0 + seg.y1);
              const x2s = sx(fpX0 + seg.x2), y2s = sy(fpY0 + seg.y2);
              const tubeW = Math.max(3, ((spec.tubeDiamMm ?? 75) / 1000) * scale);
              return (
                <g key={`tube-${fp.mi}-${i}`}>
                  {/* Plume curtain - perpendicular to tube */}
                  {(() => {
                    const dx = seg.x2 - seg.x1, dy = seg.y2 - seg.y1;
                    const len = Math.hypot(dx, dy) || 1;
                    const nx = -dy / len, ny = dx / len;
                    const hw = r_sp_px * 0.7;
                    const p1x = x1s + nx * hw, p1y = y1s + ny * hw;
                    const p2x = x1s - nx * hw, p2y = y1s - ny * hw;
                    const p3x = x2s - nx * hw, p3y = y2s - ny * hw;
                    const p4x = x2s + nx * hw, p4y = y2s + ny * hw;
                    return (
                      <polygon points={`${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y} ${p4x},${p4y}`}
                        fill={CYAN} fillOpacity={0.10} stroke={CYAN} strokeOpacity={0.3} strokeWidth={0.4} />
                    );
                  })()}
                  {/* Tube body */}
                  <line x1={x1s} y1={y1s} x2={x2s} y2={y2s}
                    stroke="#0e7490" strokeWidth={tubeW} strokeLinecap="round" />
                  {/* Pore dots */}
                  {(() => {
                    const nDots = 10;
                    const dots: React.ReactNode[] = [];
                    for (let p = 1; p <= nDots; p++) {
                      const t = p / (nDots + 1);
                      dots.push(<circle key={p}
                        cx={x1s + (x2s - x1s) * t} cy={y1s + (y2s - y1s) * t}
                        r={1} fill="#0c4a6e" />);
                    }
                    return dots;
                  })()}
                </g>
              );
            });
          });
        }
        // ─── DISC rendering (original) ───
        return allDiffusers.map((d, i) => {
          const cx = sx(d.wx), cy = sy(d.wy);
          return (
            <g key={`diff-${i}`}>
              {/* Surface bubble size circle (expanded at waterline) */}
              <circle cx={cx} cy={cy} r={Math.max(diffR * 1.4, r_sp_px * 0.6)}
                fill={CYAN} fillOpacity={0.08}
                stroke={CYAN} strokeOpacity={0.4} strokeWidth={0.5} />
              {/* Diffuser disc body */}
              <circle cx={cx} cy={cy} r={diffR}
                fill="#3b0764" stroke={VIOLET} strokeWidth={1.2} />
              {/* Membrane hatching */}
              {[...Array(3)].map((_, li) => (
                <line key={li}
                  x1={cx - diffR * 0.6} y1={cy - diffR * 0.3 + li * diffR * 0.3}
                  x2={cx + diffR * 0.6} y2={cy - diffR * 0.3 + li * diffR * 0.3}
                  stroke="#7c3aed" strokeWidth={0.6} opacity={0.5} />
              ))}
              {/* Bubble plume (upward) */}
              <ellipse cx={cx} cy={cy} rx={diffR * 0.7} ry={diffR * 2.2}
                fill={VIOLET} fillOpacity={0.06} />
              {/* Diffuser label */}
            {cfg.showLabels && scale > 45 && (
              <text x={cx} y={cy + diffR + Math.max(5, scale * 0.06)}
                textAnchor="middle" fontSize={Math.max(5, scale * 0.08)} fill="#6d28d9">
                D{d.di + 1}
              </text>
            )}
          </g>
        );
        });
      })()}

      {/* ── Gap dimension annotations ─────────────────────────────────────── */}
      {framePositions.map((fp, mi) => {
        const elements: React.ReactElement[] = [];
        // Horizontal gap to right neighbour
        if (mi % cols < cols - 1 && mi + 1 < framePositions.length) {
          const nextFp = framePositions[mi + 1];
          const x1 = sx(fp.fx + FRAME_SIZE), x2 = sx(nextFp.fx);
          const midY = sy(fp.fy + FRAME_SIZE / 2);
          if (x2 - x1 > 8) {
            elements.push(
              <g key={`gh-${mi}`}>
                <line x1={x1} y1={midY} x2={x2} y2={midY}
                  stroke={ORANGE} strokeWidth={1} strokeDasharray="2,2" opacity={0.7} />
                <polygon points={`${x1},${midY-3} ${x1+5},${midY} ${x1},${midY+3}`}
                  fill={ORANGE} opacity={0.7} />
                <polygon points={`${x2},${midY-3} ${x2-5},${midY} ${x2},${midY+3}`}
                  fill={ORANGE} opacity={0.7} />
                <text x={(x1 + x2) / 2} y={midY - 5} textAnchor="middle"
                  fontSize={Math.max(8, scale * 0.13)} fill={ORANGE}>
                  {cfg.gapXM.toFixed(1)} m
                </text>
              </g>
            );
          }
        }
        // Vertical gap to bottom neighbour
        if (mi + cols < framePositions.length) {
          const nextFp = framePositions[mi + cols];
          const y1 = sy(fp.fy + FRAME_SIZE), y2 = sy(nextFp.fy);
          const midX = sx(fp.fx + FRAME_SIZE / 2);
          if (y2 - y1 > 8) {
            elements.push(
              <g key={`gv-${mi}`}>
                <line x1={midX} y1={y1} x2={midX} y2={y2}
                  stroke="#a78bfa" strokeWidth={1} strokeDasharray="2,2" opacity={0.7} />
                <polygon points={`${midX-3},${y1} ${midX},${y1+5} ${midX+3},${y1}`}
                  fill="#a78bfa" opacity={0.7} />
                <polygon points={`${midX-3},${y2} ${midX},${y2-5} ${midX+3},${y2}`}
                  fill="#a78bfa" opacity={0.7} />
                <text x={midX + 8} y={(y1 + y2) / 2 + 4}
                  fontSize={Math.max(8, scale * 0.13)} fill="#a78bfa">
                  {cfg.gapYM.toFixed(1)} m
                </text>
              </g>
            );
          }
        }
        return elements;
      })}

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <rect x={SVG_W - 148} y={SVG_H - 68} width={142} height={62}
        fill="#0f172a" fillOpacity={0.85} rx={6} stroke="#1e293b" strokeWidth={1} />
      <circle cx={SVG_W - 136} cy={SVG_H - 51} r={5} fill="#3b0764" stroke={VIOLET} strokeWidth={1} />
      <text x={SVG_W - 128} y={SVG_H - 47} fontSize={8} fill="#94a3b8">Diffuser (bed)</text>
      <circle cx={SVG_W - 136} cy={SVG_H - 37} r={5} fill={CYAN} fillOpacity={0.15}
        stroke={CYAN} strokeWidth={0.8} strokeDasharray="2,2" />
      <text x={SVG_W - 128} y={SVG_H - 33} fontSize={8} fill="#94a3b8">Surface plume ∅</text>
      <line x1={SVG_W - 140} y1={SVG_H - 22} x2={SVG_W - 130} y2={SVG_H - 22}
        stroke={FRAME_C} strokeWidth={0.8} strokeDasharray="3,3" opacity={0.5} />
      <text x={SVG_W - 128} y={SVG_H - 18} fontSize={8} fill="#94a3b8">Influence zone</text>

      {/* ── Frame size scale bar ─────────────────────────────────────────────── */}
      <line x1={offX} y1={offY - 16} x2={offX + FRAME_SIZE * scale} y2={offY - 16}
        stroke="#475569" strokeWidth={1.5} />
      <line x1={offX} y1={offY - 20} x2={offX} y2={offY - 12} stroke="#475569" strokeWidth={1.5} />
      <line x1={offX + FRAME_SIZE * scale} y1={offY - 20} x2={offX + FRAME_SIZE * scale} y2={offY - 12}
        stroke="#475569" strokeWidth={1.5} />
      <text x={offX + FRAME_SIZE * scale / 2} y={offY - 21} textAnchor="middle"
        fontSize={9} fill="#64748b">2.0 m frame</text>
    </svg>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function Card({ title, icon: Icon, iconColor, children }:
  { title: string; icon: React.ElementType; iconColor: string; children: React.ReactNode }) {
  return (
    <div className="rb-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function NI({ label, value, onChange, min, max, step, unit, tip }:
  { label: string; value: number; onChange: (v: number) => void;
    min?: number; max?: number; step?: number; unit?: string; tip?: string }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
        {label}{unit && <span className="font-normal text-slate-600 ml-1">({unit})</span>}
      </label>
      {tip && <p className="text-[9px] text-slate-600 mb-1">{tip}</p>}
      <input type="number" value={value} step={step ?? 0.1} min={min} max={max}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500" />
    </div>
  );
}

function Kpi({ label, val, unit, col, warn }:
  { label: string; val: string; unit: string; col: string; warn?: boolean }) {
  return (
    <div className={`rb-section px-3 py-2 relative ${warn ? 'border-amber-500/40' : ''}`}>
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-bold font-mono" style={{ color: col }}>{val}</div>
      <div className="text-[10px] text-slate-600">{unit}</div>
      {warn && <AlertTriangle className="absolute top-2 right-2 w-3 h-3 text-amber-400" />}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ModuleArrayPage() {
  const navigate = useNavigate();
  const fmt = (v: number, dp = 2) => (!isFinite(v) || isNaN(v)) ? '—' : v.toFixed(dp);

  // Seasonal profile (shared across pages)
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);
  void SEASONAL_PROFILES; // exported but reserved for future use here

  // Load module spec from DiffuserModulePage sessionStorage
  const [spec] = useState<ModuleSpec>(() => {
    try {
      const raw = sessionStorage.getItem(MODULE_KEY);
      return raw ? { ...MODULE_DEFAULTS, ...JSON.parse(raw) } : MODULE_DEFAULTS;
    } catch { return MODULE_DEFAULTS; }
  });

  // Array configuration state
  const [cfg, setCfg] = useState<ArrayConfig>(() => {
    try {
      const raw = sessionStorage.getItem(ARRAY_KEY);
      return raw ? { ...ARRAY_DEFAULTS, ...JSON.parse(raw) } : ARRAY_DEFAULTS;
    } catch { return ARRAY_DEFAULTS; }
  });

  const upd = (p: Partial<ArrayConfig>) => setCfg(prev => ({ ...prev, ...p }));

  useEffect(() => {
    try { sessionStorage.setItem(ARRAY_KEY, JSON.stringify(cfg)); } catch {}
  }, [cfg]);

  const [showInteraction, setShowInteraction] = useState(true);

  // ── Physics calculations ──────────────────────────────────────────────────
  const R = useMemo(() => {
    const T          = spec.waterTempC;
    const count      = spec.diffuserCount;
    const corner     = clampCornerOffset(spec.cornerOffsetM);
    const localPos   = diffuserLayout(count, corner);
    const cols       = Math.max(1, Math.min(3, cfg.cols));
    const rows       = Math.ceil(Math.max(2, Math.min(6, cfg.moduleCount)) / cols);
    const numModules = cols * rows;

    // Frame positions in world coords
    const framePosArr: FramePos[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const mi = r * cols + c;
        if (mi >= numModules) break;
        framePosArr.push({ fx: c * (FRAME_SIZE + cfg.gapXM), fy: r * (FRAME_SIZE + cfg.gapYM), mi });
      }
    }

    // All diffuser world positions
    const allDiffusers: WorldDiff[] = framePosArr.flatMap(fp =>
      localPos.map((lp, di) => ({ wx: fp.fx + lp.x, wy: fp.fy + lp.y, mi: fp.mi, di }))
    );

    // Layout geometry
    const s_min     = minDiffSpacingM(count, corner);
    const r_inf     = s_min / 2;
    const edgeCover = Math.max(0, r_inf - corner);
    const effPitch  = FRAME_SIZE + 2 * edgeCover;

    // Effective submergence & physics
    const effSubm   = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);
    const rho_w     = waterDensity(T);
    const P_bed     = P_ATM + rho_w * G * effSubm;

    // Surface plume footprint radius (Seol cone spreading, half-angle ~5.7°)
    const r_plume_surface = 0.1 * effSubm;

    // Bubble physics
    const dtype      = DIFF_TYPES.find(t => t.value === spec.diffuserType) ?? DIFF_TYPES[0];
    const activePores= Math.max(1, dtype.pores(spec.diffuserDiamMm));
    const d_b_mm     = bubbleDiamMm(spec.poreSizeUm, spec.airflowPerDiffNm3h, activePores, T);

    // Disturbance physics
    const q_act_bed  = (spec.airflowPerDiffNm3h / 3600) * (P_ATM / P_bed);
    const B_buoy     = G * q_act_bed;
    const U_plume    = 1.46 * Math.pow(B_buoy * effSubm, 1/3);
    const v_bed      = 0.15 * U_plume / Math.sqrt(Math.max(effSubm, 1));
    const d50_m      = spec.sedimentD50Um / 1e6;
    const C_D_bed    = 0.01;
    const tau_bed    = rho_w * C_D_bed * v_bed ** 2;
    const shields    = tau_bed / ((spec.sedimentRho - rho_w) * G * Math.max(d50_m, 1e-8));
    const shields_cr = 0.047;
    const disturbRatio= shields / shields_cr;

    // Bubble correction from disturbance
    const turbCorr   = Math.min(0.15, 0.08 * Math.min(disturbRatio, 2));
    const d_b_dist   = d_b_mm * (1 + turbCorr);
    const d_b_surf   = bubbleSurface(d_b_dist, effSubm, T);

    // Per-module O₂ transfer
    const sote_pct   = Math.min(99, dtype.sote * effSubm);
    const modAirNm3h = count * spec.airflowPerDiffNm3h;
    const modSOTR    = calcSOTR(modAirNm3h, sote_pct / 100);
    const modAOTR    = calcAOTR(modSOTR, spec.alphaFactor, spec.betaFactor, T, spec.currentDoMgL);

    // Array totals
    const totalAirNm3h = numModules * modAirNm3h;
    const totalAOTR    = numModules * modAOTR;
    const blowerPow    = blowerKw(totalAirNm3h, effSubm, spec.diffuserDropKpa, spec.safetyPct, spec.blowerEff);

    // Coverage
    const arrayFootprintM2   = (cols * (FRAME_SIZE + cfg.gapXM) - cfg.gapXM) *
                                (rows * (FRAME_SIZE + cfg.gapYM) - cfg.gapYM); // actual extent
    const influenceAreaM2    = effPitch ** 2;  // per module
    const totalInfluenceM2   = numModules * influenceAreaM2;
    const coveredAreaM2      = Math.min(totalInfluenceM2, cfg.reservoirAreaHa * 1e4);
    const coveragePct        = (coveredAreaM2 / (cfg.reservoirAreaHa * 1e4)) * 100;
    const modulesPerHa       = 10000 / influenceAreaM2;

    // SOD demand with disturbance
    const sodResuspFactor = disturbRatio > 1 ? Math.min(3.0, 1 + 0.5*(disturbRatio-1)) : 1.0;
    const effectiveSOD    = spec.sodGO2m2d * sodResuspFactor;
    const sodKgO2h        = (effectiveSOD * coveredAreaM2 / 1000) / 24;
    const oxygenSurplus   = totalAOTR - sodKgO2h;

    // ── Inter-module interaction at gap centres ──────────────────────────────
    // For each horizontal adjacent pair of frames: compute gap-centre Shields
    const gapAnalyses: {
      label: string; gapM: number; nearestDistM: number;
      v_combined: number; combined_shields: number; overlapM: number; risk: string;
    }[] = [];

    // Linear velocity decay model: v_contribution(r) = v_bed × max(0, 1 - r/r_inf)
    const velContrib = (distM: number) => v_bed * Math.max(0, 1 - distM / Math.max(r_inf, 0.001));

    // Horizontal gaps
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const miA = r * cols + c;
        const miB = r * cols + c + 1;
        if (miB >= numModules) continue;
        const fpA = framePosArr[miA], fpB = framePosArr[miB];
        // Gap centre in world coords
        const gx  = fpA.fx + FRAME_SIZE + cfg.gapXM / 2;
        const gy  = fpA.fy + FRAME_SIZE / 2;
        // Sum velocity contributions from all diffusers of frames A and B
        let v_comb = 0;
        allDiffusers
          .filter(d => d.mi === miA || d.mi === miB)
          .forEach(d => {
            const dist = Math.sqrt((d.wx - gx) ** 2 + (d.wy - gy) ** 2);
            v_comb += velContrib(dist);
          });
        const tau_c    = rho_w * C_D_bed * v_comb ** 2;
        const sh_c     = tau_c / ((spec.sedimentRho - rho_w) * G * Math.max(d50_m, 1e-8));
        const overlapM = 2 * r_inf - (cfg.gapXM + 2 * corner);
        gapAnalyses.push({
          label: `H gap M${miA+1}–M${miB+1}`,
          gapM: cfg.gapXM,
          nearestDistM: cfg.gapXM / 2 + corner,
          v_combined: v_comb,
          combined_shields: sh_c,
          overlapM,
          risk: sh_c / shields_cr > 1.5 ? 'High' : sh_c / shields_cr > 1.0 ? 'Moderate' : sh_c / shields_cr > 0.5 ? 'Low' : 'Negligible',
        });
      }
    }
    // Vertical gaps
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols; c++) {
        const miA = r * cols + c;
        const miB = (r+1) * cols + c;
        if (miB >= numModules) continue;
        const fpA = framePosArr[miA], fpB = framePosArr[miB];
        const gx  = fpA.fx + FRAME_SIZE / 2;
        const gy  = fpA.fy + FRAME_SIZE + cfg.gapYM / 2;
        let v_comb = 0;
        allDiffusers
          .filter(d => d.mi === miA || d.mi === miB)
          .forEach(d => {
            const dist = Math.sqrt((d.wx - gx) ** 2 + (d.wy - gy) ** 2);
            v_comb += velContrib(dist);
          });
        const tau_c    = rho_w * C_D_bed * v_comb ** 2;
        const sh_c     = tau_c / ((spec.sedimentRho - rho_w) * G * Math.max(d50_m, 1e-8));
        const overlapM = 2 * r_inf - (cfg.gapYM + 2 * corner);
        gapAnalyses.push({
          label: `V gap M${miA+1}–M${miB+1}`,
          gapM: cfg.gapYM,
          nearestDistM: cfg.gapYM / 2 + corner,
          v_combined: v_comb,
          combined_shields: sh_c,
          overlapM,
          risk: sh_c / shields_cr > 1.5 ? 'High' : sh_c / shields_cr > 1.0 ? 'Moderate' : sh_c / shields_cr > 0.5 ? 'Low' : 'Negligible',
        });
      }
    }

    // ── Recommendations ────────────────────────────────────────────────────
    const optGap     = Math.max(0, 2 * edgeCover);   // gap for full coverage (no holes)
    const hasGapZones = cfg.gapXM > optGap + 0.05 || cfg.gapYM > optGap + 0.05;
    const hasOverlap  = gapAnalyses.some(g => g.overlapM > 0);
    const maxShields  = Math.max(0, ...gapAnalyses.map(g => g.combined_shields));

    return {
      localPos, framePosArr, allDiffusers,
      cols, rows, numModules,
      effSubm, rho_w, r_inf, edgeCover, effPitch, r_plume_surface,
      d_b_mm, d_b_dist, d_b_surf, sote_pct,
      modAirNm3h, modSOTR, modAOTR,
      totalAirNm3h, totalAOTR, blowerPow,
      influenceAreaM2, totalInfluenceM2, coveredAreaM2, coveragePct,
      modulesPerHa, arrayFootprintM2,
      shields, disturbRatio, v_bed, sodResuspFactor, effectiveSOD, sodKgO2h, oxygenSurplus,
      gapAnalyses, optGap, hasGapZones, hasOverlap, maxShields, shields_cr, turbCorr,
    };
  }, [spec, cfg]);

  // ─── PDF download ─────────────────────────────────────────────────────────
  const downloadPDF = () => {
    const isTubeType = spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube';
    const poresPerUnit = isTubeType
      ? Math.max(1, Math.round((spec.porePerCm ?? 8) * (spec.tubeLengthM ?? 1.8) * 100))
      : Math.round(0.35 * Math.PI * (spec.diffuserDiamMm / 2) ** 2);
    const unitsPerMod = isTubeType ? (spec.tubeCount ?? 2) : spec.diffuserCount;
    generateModuleArrayPDF({
      spec: {
        diffuserCount: spec.diffuserCount, diffuserType: spec.diffuserType,
        diffuserDiamMm: spec.diffuserDiamMm, poreSizeUm: spec.poreSizeUm,
        airflowPerDiffNm3h: spec.airflowPerDiffNm3h, mountingHeightM: spec.mountingHeightM,
        waterTempC: spec.waterTempC, currentDoMgL: spec.currentDoMgL,
        alphaFactor: spec.alphaFactor, betaFactor: spec.betaFactor,
        cornerOffsetM: spec.cornerOffsetM, diffuserDropKpa: spec.diffuserDropKpa,
        blowerEff: spec.blowerEff, safetyPct: spec.safetyPct,
        waterDepthM: spec.waterDepthM,
        tubeLengthM: spec.tubeLengthM, tubeCount: spec.tubeCount,
        tubeDiamMm: spec.tubeDiamMm, tubePattern: spec.tubePattern,
        tubeCentreOffsetM: spec.tubeCentreOffsetM, porePerCm: spec.porePerCm,
      },
      config: {
        moduleCount: R.numModules, cols: R.cols, rows: R.rows,
        gapXM: cfg.gapXM, gapYM: cfg.gapYM,
        arrayWidthM: R.cols * FRAME_SIZE + (R.cols - 1) * cfg.gapXM,
        arrayHeightM: R.rows * FRAME_SIZE + (R.rows - 1) * cfg.gapYM,
        reservoirAreaHa: cfg.reservoirAreaHa,
      },
      physics: {
        poresPerUnit, unitsPerModule: unitsPerMod, poresPerModule: poresPerUnit * unitsPerMod,
        qPerModule: R.modAirNm3h, d_b: R.d_b_mm, v_rise: 0,
        soteM: R.sote_pct / Math.max(0.1, R.effSubm),
        sotrPerModule: R.modSOTR, aotrPerModule: R.modAOTR,
        kwPerModule: R.blowerPow / Math.max(1, R.numModules),
        r_inf_bed: R.r_inf, r_surface: R.r_plume_surface, effSubmM: R.effSubm,
      },
      totals: {
        diffusers: R.numModules * unitsPerMod, airflow: R.totalAirNm3h,
        sotr: R.numModules * R.modSOTR, aotr: R.totalAOTR, kw: R.blowerPow,
      },
    }, profile);
    toast.success(`Module Array PDF downloaded (${profile.label})`);
  };

  const riskColor = (ratio: number) =>
    ratio > 1.5 ? 'text-red-400' : ratio > 1.0 ? 'text-amber-400' : ratio > 0.5 ? 'text-yellow-400' : 'text-emerald-400';
  const riskBg = (ratio: number) =>
    ratio > 1.5 ? 'bg-red-500/8 border-red-500/30' : ratio > 1.0 ? 'bg-amber-500/8 border-amber-500/30'
    : ratio > 0.5 ? 'bg-yellow-500/8 border-yellow-500/30' : 'bg-emerald-500/5 border-emerald-500/20';

  const LAYOUT_OPTIONS = [
    { cols: 1, label: '1 × N (column)' },
    { cols: 2, label: '2 × N (pairs)' },
    { cols: 3, label: '3 × N (rows)' },
  ];

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      <PageHeader
        icon={Grid3x3}
        iconColor="text-cyan-400"
        title="Module Array Visualization"
        subtitle={`${R.numModules}× ${spec.diffuserCount}-diffuser modules — plan view with surface plume & interaction analysis`}
        badge={
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-800/40">
            {R.numModules} modules · {R.numModules * spec.diffuserCount} diffusers
          </span>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadPDF}
              className="px-3 py-1.5 text-xs font-bold text-slate-900 bg-rb-brand hover:bg-rb-brand/90 rounded-lg flex items-center gap-1.5 shadow">
              <FileText className="w-3.5 h-3.5" /> Download Array PDF
            </button>
            <select value={seasonId} onChange={e => setSeasonId(e.target.value)}
              className="px-2 py-1.5 text-[10px] font-bold text-slate-200 bg-rb-elevated border border-slate-700/60 rounded-lg">
              {SEASONAL_PROFILES.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <button onClick={() => navigate('/diffuser-validation')}
              className="px-3 py-1.5 text-xs font-bold text-sky-300 bg-rb-elevated border border-sky-700/60 hover:border-sky-500 rounded-lg flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Validation Report
            </button>
            <button onClick={() => navigate('/diffuser-module')}
              className="px-3 py-1.5 text-xs font-bold text-violet-300 bg-rb-elevated border border-violet-700/60 hover:border-violet-500 rounded-lg flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Diffuser Module
            </button>
            <button onClick={() => navigate('/reservoir-aeration-spec')}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <Wind className="w-3.5 h-3.5" /> Aeration Spec
            </button>
            <button onClick={() => { setCfg(ARRAY_DEFAULTS); toast.info('Array reset to defaults'); }}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset Array
            </button>
            <button onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>
        }
      />

      {/* Data source banner */}
      <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/20 text-xs text-violet-200/70 flex items-center gap-2">
        <Info className="w-4 h-4 text-violet-400 shrink-0" />
        Module design loaded from Diffuser Module page:
        <strong className="text-violet-300 ml-1">
          {spec.diffuserCount} diffusers / frame · corner offset {spec.cornerOffsetM.toFixed(2)} m · {spec.waterTempC} °C · {spec.sodGO2m2d} g O₂/m²/d SOD
        </strong>
        {' — '}
        <button onClick={() => navigate('/diffuser-module')}
          className="underline text-violet-400 hover:text-violet-300">
          adjust in Diffuser Module page
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="Modules"          val={String(R.numModules)}           unit={`${R.cols} × ${R.rows} array`}    col="#06b6d4" />
        <Kpi label="Total diffusers"  val={String(R.numModules * spec.diffuserCount)} unit="ea total"            col="#8b5cf6" />
        <Kpi label="Bubble ∅ surface" val={fmt(R.d_b_surf, 3)}             unit="mm at waterline"                 col="#a78bfa" warn={R.d_b_surf > 2.5} />
        <Kpi label="r_plume surface"  val={fmt(R.r_plume_surface, 3)}      unit="m plume radius"                  col="#38bdf8" />
        <Kpi label="Total AOTR"       val={fmt(R.totalAOTR, 2)}            unit="kgO₂/h"                         col="#f59e0b" />
        <Kpi label="SOD demand"       val={fmt(R.sodKgO2h, 3)}             unit="kgO₂/h (disturbed)"             col={R.oxygenSurplus >= 0 ? '#10b981' : '#ef4444'} warn={R.oxygenSurplus < 0} />
        <Kpi label="Coverage"         val={fmt(R.coveragePct, 1)}          unit={`% of ${cfg.reservoirAreaHa} ha`} col="#22c55e" warn={R.coveragePct < 60} />
        <Kpi label="Array blower"     val={fmt(R.blowerPow, 1)}            unit="kW total"                        col="#ef4444" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">

        {/* LEFT: SVG plan view (3/5 width) */}
        <div className="xl:col-span-3 space-y-4">
          <Card title="Array Plan View — Top Down (bed level)" icon={Grid3x3} iconColor="text-cyan-400">
            {/* Display toggles */}
            <div className="flex gap-3 mb-3 flex-wrap">
              {[
                { key: 'showSurface'   as const, label: 'Surface plume ∅', col: 'text-cyan-400' },
                { key: 'showInfluence' as const, label: 'Influence radius', col: 'text-sky-400' },
                { key: 'showLabels'    as const, label: 'Labels',           col: 'text-slate-400' },
              ].map(t => (
                <button key={t.key}
                  onClick={() => upd({ [t.key]: !cfg[t.key] })}
                  className={`px-3 py-1 rounded-lg text-xs font-bold border transition-all ${
                    cfg[t.key]
                      ? 'bg-slate-700/40 border-slate-600/60 ' + t.col
                      : 'bg-rb-elevated border-slate-800/60 text-slate-600'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <ArraySVG
              spec={spec}
              cfg={cfg}
              framePositions={R.framePosArr}
              localPositions={R.localPos}
              allDiffusers={R.allDiffusers}
              r_inf={R.r_inf}
              r_plume_surface={R.r_plume_surface}
              effSubm={R.effSubm}
            />

            {/* Surface plume info */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] text-slate-500">
              <div>Bubble ∅ bed<br/><strong className="text-violet-300 font-mono">{fmt(R.d_b_dist, 3)} mm</strong> <span className="text-orange-400">(disturbed)</span></div>
              <div>Bubble ∅ surface<br/><strong className="text-cyan-300 font-mono">{fmt(R.d_b_surf, 3)} mm</strong> <span className="text-slate-500">(×{fmt(R.d_b_surf/Math.max(R.d_b_dist,0.001),2)} exp.)</span></div>
              <div>Plume footprint r<br/><strong className="text-sky-300 font-mono">{fmt(R.r_plume_surface, 3)} m</strong> at surface</div>
              <div>Turb. corr. (disturbance)<br/><strong className="text-amber-300 font-mono">+{fmt(R.turbCorr * 100, 1)}%</strong> on bubble ∅</div>
            </div>
          </Card>

          {/* Array footprint summary */}
          <Card title="Array Footprint & Coverage" icon={Database} iconColor="text-emerald-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              {[
                { label: 'Array footprint',  val: fmt(R.arrayFootprintM2 / 1e4, 3), unit: 'ha physical',    col: '#64748b' },
                { label: 'Influence / mod',  val: fmt(R.influenceAreaM2, 2),         unit: 'm² / module',   col: '#06b6d4' },
                { label: 'Total influence',  val: fmt(R.totalInfluenceM2 / 1e4, 2),  unit: 'ha influence',  col: '#38bdf8' },
                { label: 'Effective pitch',  val: fmt(R.effPitch, 3),                unit: 'm pitch',       col: '#a78bfa' },
                { label: 'Modules / ha',     val: fmt(R.modulesPerHa, 1),            unit: 'modules/ha',    col: '#f97316' },
                { label: 'O₂ surplus',       val: (R.oxygenSurplus >= 0 ? '+' : '') + fmt(R.oxygenSurplus, 3), unit: 'kgO₂/h', col: R.oxygenSurplus >= 0 ? '#10b981' : '#ef4444' },
              ].map(k => (
                <div key={k.label} className="rb-section px-3 py-2">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</div>
                  <div className="text-base font-bold font-mono" style={{ color: k.col }}>{k.val}</div>
                  <div className="text-[10px] text-slate-600">{k.unit}</div>
                </div>
              ))}
            </div>
            {/* O₂ balance bar */}
            <div className="space-y-1.5 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">O₂ Mass Balance (disturbed SOD)</div>
              {[
                { label: 'SOD (nominal)',   val: (spec.sodGO2m2d * R.coveredAreaM2 / 1000) / 24, col: 'bg-orange-500' },
                { label: 'SOD (disturbed)', val: R.sodKgO2h,  col: 'bg-red-500' },
                { label: 'Total AOTR',      val: R.totalAOTR, col: 'bg-emerald-500' },
              ].map(row => (
                <div key={row.label} className="flex items-center gap-2">
                  <div className="text-[9px] text-slate-400 w-28 shrink-0">{row.label}</div>
                  <div className="flex-1 h-2 bg-rb-bg rounded-full overflow-hidden">
                    <div className={`h-full ${row.col} opacity-70 rounded-full`}
                      style={{ width: `${Math.min(100, (row.val / Math.max(R.sodKgO2h, R.totalAOTR, 0.001)) * 100)}%` }} />
                  </div>
                  <div className="text-[9px] font-mono text-slate-300 w-16 text-right">{fmt(row.val, 3)} kg/h</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT: Controls + interaction + recommendations (2/5 width) */}
        <div className="xl:col-span-2 space-y-4">

          {/* Array configuration */}
          <Card title="Array Configuration" icon={Grid3x3} iconColor="text-violet-400">
            {/* Module count selector */}
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Modules in Array</div>
              <div className="flex gap-2">
                {[2, 3, 4, 5, 6].map(n => (
                  <button key={n} onClick={() => upd({ moduleCount: n })}
                    className={`flex-1 py-2 rounded-xl text-sm font-bold border transition-all ${
                      cfg.moduleCount === n
                        ? 'bg-cyan-500/15 border-cyan-500/60 text-cyan-300'
                        : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                    }`}>{n}</button>
                ))}
              </div>
            </div>

            {/* Columns (layout) */}
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Layout</div>
              <div className="flex gap-2">
                {LAYOUT_OPTIONS.map(opt => (
                  <button key={opt.cols} onClick={() => upd({ cols: opt.cols })}
                    className={`flex-1 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                      cfg.cols === opt.cols
                        ? 'bg-violet-500/15 border-violet-500/60 text-violet-300'
                        : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                    }`}>{opt.label}</button>
                ))}
              </div>
            </div>
            <div className="text-[10px] text-slate-600 mb-3">
              → {R.cols} column{R.cols > 1 ? 's' : ''} × {R.rows} row{R.rows > 1 ? 's' : ''} = {R.numModules} modules
            </div>

            <div className="grid grid-cols-2 gap-3">
              <NI label="Horizontal gap" unit="m" value={cfg.gapXM}
                onChange={v => upd({ gapXM: Math.max(0, v) })} step={0.25} min={0} max={20}
                tip="Edge-to-edge distance between frames horizontally" />
              <NI label="Vertical gap" unit="m" value={cfg.gapYM}
                onChange={v => upd({ gapYM: Math.max(0, v) })} step={0.25} min={0} max={20}
                tip="Edge-to-edge distance between frames vertically" />
              <NI label="Reservoir area" unit="ha" value={cfg.reservoirAreaHa}
                onChange={v => upd({ reservoirAreaHa: Math.max(0.1, v) })} step={0.5} min={0.1}
                tip="Total reservoir area — used for coverage % and SOD demand" />
            </div>

            {/* Derived geometry */}
            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-[10px] text-slate-500 grid grid-cols-2 gap-1.5">
              <div>Influence radius <strong className="text-cyan-400 font-mono">{fmt(R.r_inf, 3)} m</strong></div>
              <div>Edge cover <strong className={R.edgeCover > 0 ? 'text-emerald-400' : 'text-slate-600'}>
                {R.edgeCover > 0 ? `+${fmt(R.edgeCover, 3)} m` : 'within frame'}
              </strong></div>
              <div>Optimal gap <strong className="text-amber-400 font-mono">{fmt(R.optGap, 2)} m</strong></div>
              <div>Current gap <strong className={R.hasGapZones ? 'text-red-400' : 'text-emerald-400'}>
                {R.hasGapZones ? 'has gap zones' : 'full coverage'}
              </strong></div>
            </div>
          </Card>

          {/* Inter-module interaction */}
          <Card title="Inter-Module Interaction" icon={Activity} iconColor="text-orange-400">
            <button onClick={() => setShowInteraction(s => !s)}
              className="mb-3 flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300">
              {showInteraction ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showInteraction ? 'Collapse' : 'Expand'}
            </button>
            {showInteraction && (
              <>
                {R.gapAnalyses.length === 0 ? (
                  <div className="text-xs text-slate-500 py-4 text-center">
                    No adjacent pairs — increase module count or add more columns/rows
                  </div>
                ) : (
                  <div className="space-y-2">
                    {R.gapAnalyses.map((ga, i) => {
                      const shRatio = ga.combined_shields / R.shields_cr;
                      return (
                        <div key={i} className={`p-2.5 rounded-xl border text-xs ${riskBg(shRatio)}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-bold text-slate-300 text-[11px]">{ga.label}</span>
                            <span className={`text-[10px] font-bold ${riskColor(shRatio)}`}>
                              {ga.risk} (θ/θ_cr = {fmt(shRatio, 2)})
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-slate-400">
                            <div>Gap <strong className="text-slate-200 font-mono">{fmt(ga.gapM, 2)} m</strong></div>
                            <div>Nearest diffuser <strong className="text-slate-200 font-mono">{fmt(ga.nearestDistM, 3)} m</strong></div>
                            <div>v_combined <strong className="text-amber-300 font-mono">{fmt(ga.v_combined * 1000, 2)} mm/s</strong></div>
                            <div>Combined Shields <strong className={riskColor(shRatio) + ' font-mono'}>{fmt(ga.combined_shields, 4)}</strong></div>
                            <div className="col-span-2">
                              {ga.overlapM > 0.005
                                ? <span className="text-emerald-400">✓ Influence overlap: +{fmt(ga.overlapM, 3)} m — full coverage in gap</span>
                                : <span className="text-amber-400">↔ Gap zone: {fmt(-ga.overlapM, 3)} m uncovered — plumes do not reach each other</span>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Combined Shields summary */}
                <div className={`mt-3 p-3 rounded-xl border text-xs ${riskBg(R.maxShields / R.shields_cr)}`}>
                  <div className="font-bold mb-1" style={{ color: R.maxShields / R.shields_cr > 1 ? '#f59e0b' : '#10b981' }}>
                    Max combined Shields in any gap: {fmt(R.maxShields, 4)} (θ_cr = {fmt(R.shields_cr, 3)})
                  </div>
                  <div className="text-slate-400">
                    {R.maxShields > R.shields_cr
                      ? 'Gap zones experience combined bed disturbance above critical Shields → sediment resuspension risk in gaps. Increase gap distance or reduce airflow per diffuser.'
                      : 'Combined bed shear in all gaps remains below critical Shields threshold → no sediment resuspension in gap zones.'}
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Recommendations panel */}
          <Card title="Design Recommendations" icon={CheckCircle2} iconColor="text-emerald-400">
            <div className="space-y-2 text-xs">

              {/* Gap recommendation */}
              <div className={`p-3 rounded-xl border ${R.hasGapZones ? 'bg-amber-500/5 border-amber-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                <div className={`font-bold mb-1 ${R.hasGapZones ? 'text-amber-300' : 'text-emerald-300'}`}>
                  {R.hasGapZones ? '⚠ Gap zones present' : '✓ Full coverage achieved'}
                </div>
                <div className="text-slate-400">
                  Optimal gap for full coverage: <strong className="text-amber-300 font-mono">{fmt(R.optGap, 2)} m</strong>
                  {R.optGap === 0 ? ' — frames must be placed edge-to-edge for this diffuser count and corner offset' : ''}.
                  {R.hasGapZones ? ` Current gap (${fmt(cfg.gapXM, 1)}m H / ${fmt(cfg.gapYM, 1)}m V) leaves uncovered zones. Reduce gap to ≤ ${fmt(R.optGap, 2)} m or increase corner offset.` : ' Current gap ensures influence radii meet.'}
                </div>
              </div>

              {/* Shields in gap */}
              <div className={`p-3 rounded-xl border ${riskBg(R.maxShields / R.shields_cr)}`}>
                <div className={`font-bold mb-1 ${riskColor(R.maxShields / R.shields_cr)}`}>
                  Gap Shields: {fmt(R.maxShields / R.shields_cr, 2)} × θ_cr — {
                    R.maxShields > R.shields_cr * 1.5 ? 'High risk, increase gap' :
                    R.maxShields > R.shields_cr ? 'Moderate risk, monitor' : 'Below critical ✓'
                  }
                </div>
                <div className="text-slate-400">
                  Combined induced velocity in gap zone {fmt(Math.max(0, ...R.gapAnalyses.map(g=>g.v_combined)) * 1000, 1)} mm/s.
                  {R.maxShields > R.shields_cr
                    ? ' Consider increasing gap to reduce combined bed shear in gap zone.'
                    : ' Bed disturbance in gaps remains acceptable.'}
                </div>
              </div>

              {/* O₂ balance */}
              <div className={`p-3 rounded-xl border ${R.oxygenSurplus >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/8 border-red-500/30'}`}>
                <div className={`font-bold mb-1 ${R.oxygenSurplus >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  {R.oxygenSurplus >= 0 ? `✓ O₂ surplus: +${fmt(R.oxygenSurplus, 3)} kgO₂/h` : `✗ O₂ deficit: ${fmt(R.oxygenSurplus, 3)} kgO₂/h`}
                </div>
                <div className="text-slate-400">
                  Effective SOD = {fmt(R.effectiveSOD, 2)} g/m²/d (×{fmt(R.sodResuspFactor, 2)} disturbance factor).
                  {R.oxygenSurplus < 0 ? ' Increase airflow per diffuser, add more modules, or reduce gap to improve coverage.' : ' Array meets oxygen demand including sediment resuspension enhancement.'}
                </div>
              </div>

              {/* Modules per ha */}
              <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/40">
                <div className="font-bold text-cyan-300 mb-1">Coverage geometry</div>
                <div className="text-slate-400">
                  At current gap ({fmt(cfg.gapXM, 1)} m H / {fmt(cfg.gapYM, 1)} m V):
                  effective pitch {fmt(R.effPitch, 2)} m → <strong className="text-orange-300">{fmt(R.modulesPerHa, 1)} modules/ha</strong>.
                  For {cfg.reservoirAreaHa} ha reservoir: ~{Math.ceil(R.modulesPerHa * cfg.reservoirAreaHa)} modules recommended.
                  Current {R.numModules}-module array covers {fmt(R.coveragePct, 1)}% of reservoir.
                </div>
              </div>

              {/* Overall recommendation */}
              <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/20">
                <div className="font-bold text-violet-300 mb-1">Summary recommendation</div>
                <div className="text-slate-400">
                  {R.numModules} × {spec.diffuserCount}-diffuser modules at {fmt(cfg.gapXM, 1)} m gap,
                  {spec.waterTempC} °C, {spec.sodGO2m2d} g/m²/d SOD.
                  Disturbed bubble ∅ at surface = <strong className="text-cyan-300">{fmt(R.d_b_surf, 3)} mm</strong>,
                  plume footprint = <strong className="text-sky-300">{fmt(R.r_plume_surface, 3)} m</strong> radius.
                  {R.hasGapZones && R.oxygenSurplus >= 0
                    ? ` Gap zones exist but O₂ supply is adequate. Reduce gap to ${fmt(R.optGap, 2)} m for full bed coverage.`
                    : !R.hasGapZones && R.oxygenSurplus >= 0
                    ? ' Design is optimal — full coverage, positive O₂ balance.'
                    : ' Both gap zones and O₂ deficit present — increase module count or airflow.'}
                </div>
              </div>
            </div>
          </Card>

          {/* Per-module summary (from design page) */}
          <Card title="Per-Module Summary (from design)" icon={Ruler} iconColor="text-slate-400">
            <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400">
              {[
                { k: 'Diffuser type',       v: (DIFF_TYPES.find(t=>t.value===spec.diffuserType)?.label ?? spec.diffuserType) },
                { k: 'Count / frame',        v: `${spec.diffuserCount} diffusers` },
                { k: 'Corner offset',        v: `${fmt(spec.cornerOffsetM, 2)} m` },
                { k: 'Disc diameter',        v: `${spec.diffuserDiamMm} mm` },
                { k: 'Pore size',            v: `${spec.poreSizeUm} µm` },
                { k: 'Airflow / diffuser',   v: `${spec.airflowPerDiffNm3h} Nm³/h` },
                { k: 'SOTE',                 v: `${fmt(R.sote_pct, 1)}% @ ${fmt(R.effSubm, 2)} m` },
                { k: 'Module AOTR',          v: `${fmt(R.modAOTR, 3)} kgO₂/h` },
                { k: 'Water temp',           v: `${spec.waterTempC} °C` },
                { k: 'Current DO',           v: `${spec.currentDoMgL} mg/L` },
                { k: 'Sediment D₅₀',        v: `${spec.sedimentD50Um} µm` },
                { k: 'Disturbance ratio',    v: `${fmt(R.disturbRatio, 2)} × θ_cr` },
              ].map(r => (
                <div key={r.k} className="flex justify-between py-0.5 border-b border-slate-800/30">
                  <span className="text-slate-600">{r.k}</span>
                  <strong className="text-slate-300 font-mono">{r.v}</strong>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
